/* ============================================
   SCROLL TO TOP COMPONENT
   The Creative Orchestra - GEMINI Framework
   Scroll utilities for route changes and manual trigger
   ============================================ */

import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { useTheme } from '@context/ThemeContext';

/**
 * Scroll To Top on Route Change
 * Automatically scrolls to top when route changes
 * Place this component inside Router
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo({
      top: 0,
      behavior: 'instant', // Use instant for route changes
    });
  }, [pathname]);

  return null;
};

/**
 * Scroll To Top Button
 * Floating button that appears when scrolled down
 * 
 * @param {Object} props
 * @param {number} props.threshold - Scroll threshold to show button (default: 300)
 * @param {boolean} props.smooth - Use smooth scrolling (default: true)
 */
export const ScrollToTopButton = ({ 
  threshold = 300,
  smooth = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const { activeTheme } = useTheme();
  const accentColor = activeTheme?.colors?.accent || '#818CF8';

  // Check scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > threshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  // Scroll to top handler
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: smooth ? 'smooth' : 'instant',
    });
  }, [smooth]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={scrollToTop}
          className="fixed bottom-6 right-32 z-50 w-12 h-12 rounded-full
            bg-black/60 backdrop-blur-sm border border-white/10
            flex items-center justify-center
            text-white hover:text-white hover:border-white/20
            transition-colors shadow-lg"
          style={{
            boxShadow: `0 0 20px ${accentColor}20`,
          }}
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

/**
 * Scroll Progress Indicator
 * Shows reading/scroll progress at top of page
 */
export const ScrollProgressBar = ({ 
  height = 3,
  position = 'top',
}) => {
  const [progress, setProgress] = useState(0);
  const { activeTheme } = useTheme();
  const accentColor = activeTheme?.colors?.accent || '#818CF8';

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, scrollPercent)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div 
      className={`fixed left-0 right-0 z-50 ${
        position === 'top' ? 'top-0' : 'bottom-0'
      }`}
      style={{ height }}
    >
      <motion.div
        className="h-full"
        style={{
          width: `${progress}%`,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}80)`,
        }}
        transition={{ duration: 0.1 }}
      />
    </div>
  );
};

/**
 * Scroll To Section Hook
 * Returns a function to scroll to a section by ID
 */
export const useScrollToSection = () => {
  const scrollToSection = useCallback((sectionId, offset = 80) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    }
  }, []);

  return scrollToSection;
};

/**
 * Scroll To Element Component
 * Anchor that smoothly scrolls to target element
 */
export const ScrollLink = ({ 
  to, 
  children, 
  offset = 80,
  className = '',
  onClick,
}) => {
  const handleClick = useCallback((e) => {
    e.preventDefault();
    const element = document.getElementById(to);
    if (element) {
      const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    }
    onClick?.();
  }, [to, offset, onClick]);

  return (
    <a 
      href={`#${to}`} 
      onClick={handleClick}
      className={className}
    >
      {children}
    </a>
  );
};

/**
 * Scroll Reveal Component
 * Triggers animation when element enters viewport
 */
export const ScrollReveal = ({ 
  children, 
  threshold = 0.1,
  delay = 0,
  direction = 'up',
  className = '',
}) => {
  const directions = {
    up: { y: 30 },
    down: { y: -30 },
    left: { x: 30 },
    right: { x: -30 },
    none: {},
  };

  return (
    <motion.div
      initial={{ 
        opacity: 0, 
        ...directions[direction],
      }}
      whileInView={{ 
        opacity: 1, 
        x: 0, 
        y: 0,
      }}
      viewport={{ once: true, amount: threshold }}
      transition={{ 
        duration: 0.6, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default ScrollToTop;
